/**
 * LeasingDashboardPage
 *
 * At-a-glance summary for the Leasing hub. Shows unread messages (up to 5)
 * and recent activity (messages, showings, applications) with a time-window filter.
 *
 * Route: /leasing/dashboard
 */

import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatTimestamp, formatUnitAddress, asIsoDateTimeString } from '@resida/shared';
import { cn } from '@/lib/utils';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
  fetchInboxUnresponded,
  fetchDashboardActivity,
  fetchUpcomingShowings,
  type UnrespondedMessage,
  type ActivityItem,
  type UpcomingShowingItem,
} from '../../lib/leasing.api';
import { useDomainEvents } from '../../hooks/useDomainEvents';
import { computeSince } from './dashboard-time-window';
import { computeWindowParam, SHOWINGS_TIME_WINDOW_LABELS, type ShowingsTimeWindow } from './showings-time-window';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAge(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

function formatAppointmentTime(isoString: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: timezone,
  }).format(new Date(isoString));
}

// Upcoming Showings items now carry a per-item `timezone` field from the boundary
// (derived server-side via deriveTimezone(unit.state), same as the activity feed's
// appointment_* variants) — mirrors formatAppointmentTime() above, anchoring the
// timestamp to the listing's property timezone via @resida/shared's formatTimestamp
// instead of the viewer's local time.
function formatShowingTime(isoString: string, timezone: string): string {
  return formatTimestamp(asIsoDateTimeString(isoString), timezone, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

// ---------------------------------------------------------------------------
// Inbox preview row
// ---------------------------------------------------------------------------

function InboxPreviewRow({ item }: { item: UnrespondedMessage }) {
  const addressDisplay = item.propertyAddress
    ? formatUnitAddress(item.propertyAddress, item.unitLabel, ' · ')
    : null;

  return (
    <li data-testid={`dashboard-inbox-row-${item.leadId}`}>
      <Link
        to={`/leasing/leads/${item.leadId}`}
        aria-label={`Open lead for ${item.prospectName ?? item.prospectEmail}`}
        className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 hover:bg-accent/50 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="font-medium text-foreground truncate">
              {item.prospectName ?? item.prospectEmail}
            </span>
            {item.isNewInquiry ? (
              <Badge variant="info">New inquiry</Badge>
            ) : (
              <Badge variant="secondary">New message</Badge>
            )}
          </div>
          {item.prospectName && (
            <p className="text-xs text-muted-foreground">{item.prospectEmail}</p>
          )}
          {addressDisplay && (
            <p className="text-xs text-muted-foreground mt-0.5">{addressDisplay}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Last {formatAge(item.lastInboundAt)}
          </p>
        </div>
        {item.isNewInquiry && (
          <Badge variant="danger" className="shrink-0">Needs Reply</Badge>
        )}
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Activity item row
// ---------------------------------------------------------------------------

function ActivityItemRow({ item }: { item: ActivityItem }) {
  const headline = (() => {
    switch (item.type) {
      case 'message': return `${item.prospectName ?? item.prospectEmail} sent a message`;
      case 'appointment_proposed': return `${item.prospectName ?? item.prospectEmail} requested a showing`;
      case 'appointment_rescheduled': return `${item.prospectName ?? item.prospectEmail} proposed a new time`;
      case 'application_submitted': return `${item.applicantName} submitted their application`;
    }
  })();

  const detail = (() => {
    switch (item.type) {
      case 'message': return item.snippet;
      case 'appointment_proposed':
        return `Proposed ${formatAppointmentTime(item.proposedFor, item.timezone)}`;
      case 'appointment_rescheduled':
        return `New time: ${formatAppointmentTime(item.newProposedFor, item.timezone)}`;
      case 'application_submitted': return item.listingAddress;
    }
  })();

  const secondary = item.type !== 'application_submitted' ? item.listingAddress : null;

  const tab = item.type === 'message' ? 'messages'
            : item.type === 'application_submitted' ? 'applicants'
            : 'showings';
  const to = `/leasing/leads/${item.leadId}?tab=${tab}`;

  return (
    <li data-testid={`activity-row-${item.leadId}-${item.type}`}>
      <Link
        to={to}
        className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 hover:bg-accent/50 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground truncate">{headline}</p>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{detail}</p>
          {secondary && <p className="text-xs text-muted-foreground mt-0.5">{secondary}</p>}
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">{formatAge(item.occurredAt)}</span>
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

interface DashboardSectionProps {
  title: string;
  viewAllTo: string;
  viewAllLabel: string;
  isLoading: boolean;
  isEmpty: boolean;
  emptyMessage: string;
  children: React.ReactNode;
}

function DashboardSection({
  title,
  viewAllTo,
  viewAllLabel,
  isLoading,
  isEmpty,
  emptyMessage,
  children,
}: DashboardSectionProps) {
  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">
          {title}
        </h2>
        <Link
          to={viewAllTo}
          className="text-sm text-primary hover:underline"
          data-testid={`dashboard-viewall-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {viewAllLabel}
        </Link>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      )}

      {!isLoading && isEmpty && (
        <p className="text-sm text-muted-foreground py-4">{emptyMessage}</p>
      )}

      {!isLoading && !isEmpty && (
        <ul className="space-y-2">{children}</ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Recent Activity section
// ---------------------------------------------------------------------------

interface RecentActivitySectionProps {
  timeWindow: '1h' | '4h' | 'today';
  setTimeWindow: (w: '1h' | '4h' | 'today') => void;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  items: ActivityItem[];
}

function RecentActivitySection({ timeWindow, setTimeWindow, isLoading, isError, refetch, items }: RecentActivitySectionProps) {
  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Recent Activity</h2>
        <div className="flex gap-1" data-testid="activity-window-filter">
          {(['1h', '4h', 'today'] as const).map((w) => (
            <button
              key={w}
              onClick={() => setTimeWindow(w)}
              data-testid={`activity-window-${w}`}
              aria-pressed={timeWindow === w}
              className={cn(
                'px-3 py-1 rounded-full text-xs font-medium transition-colors',
                timeWindow === w
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              {w === '1h' ? 'Last Hour' : w === '4h' ? 'Last 4 Hours' : 'Today'}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      )}

      {!isLoading && isError && (
        <div className="py-4 text-center space-y-2">
          <p className="text-sm text-muted-foreground">Unable to load activity. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="text-sm text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">No activity in this period.</p>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <ul data-testid="activity-list" className="space-y-2">
          {items.map((item) => (
            <ActivityItemRow key={`${item.leadId}-${item.type}-${item.occurredAt}`} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Upcoming Showings row
// ---------------------------------------------------------------------------

function UpcomingShowingRow({ item }: { item: UpcomingShowingItem }) {
  const to = item.leadId
    ? `/leasing/leads/${item.leadId}?tab=showings`
    : `/leasing/listings/${item.listingId}`;

  return (
    <li data-testid={`upcoming-showings-row-${item.id}`}>
      <Link
        to={to}
        aria-label={`Open showing for ${item.prospectName ?? 'Open house'} at ${item.listingAddress}`}
        className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 hover:bg-accent/50 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground truncate">
            {item.prospectName ?? 'Open house'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{item.listingAddress}</p>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">{formatShowingTime(item.scheduledFor, item.timezone)}</span>
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Upcoming Showings section
// ---------------------------------------------------------------------------

interface UpcomingShowingsSectionProps {
  timeWindow: ShowingsTimeWindow;
  setTimeWindow: (w: ShowingsTimeWindow) => void;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  items: UpcomingShowingItem[];
}

function UpcomingShowingsSection({ timeWindow, setTimeWindow, isLoading, isError, refetch, items }: UpcomingShowingsSectionProps) {
  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Upcoming Showings</h2>
        <div className="flex gap-1" data-testid="showings-window-filter">
          {(Object.keys(SHOWINGS_TIME_WINDOW_LABELS) as ShowingsTimeWindow[]).map((w) => (
            <button
              key={w}
              onClick={() => setTimeWindow(w)}
              data-testid={`showings-window-${w}`}
              aria-pressed={timeWindow === w}
              className={cn(
                'px-3 py-1 rounded-full text-xs font-medium transition-colors min-h-[44px]',
                timeWindow === w
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              {SHOWINGS_TIME_WINDOW_LABELS[w]}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      )}

      {!isLoading && isError && (
        <div className="py-4 text-center space-y-2">
          <p className="text-sm text-muted-foreground">Unable to load upcoming showings. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="text-sm text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">No upcoming showings in this period.</p>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <ul data-testid="upcoming-showings-list" className="space-y-2">
          {items.map((item) => (
            <UpcomingShowingRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LeasingDashboardPage() {
  const [timeWindow, setTimeWindow] = useState<'1h' | '4h' | 'today'>('today');
  const [showingsWindow, setShowingsWindow] = useState<ShowingsTimeWindow>('2d');
  const queryClient = useQueryClient();

  // DC-2/LS-BR-NOTIF-14 — all eight leasing DomainEvent arms feed both the unread-messages
  // preview and the recent-activity feed on this page, plus case.message_received (JR-1): a
  // new inbound message is what makes a lead unread/unresponded in the first place — without
  // this key the preview only caught up after staleTime or a WebSocket reconnect, never live.
  // 'upcoming-showings' rides the same invalidation set (Change 16) — showing.scheduled and
  // every appointment_proposal.* arm below already covers it; no new subscription needed.
  const invalidateDashboard = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['inbox-unresponded'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard-activity'] });
    void queryClient.invalidateQueries({ queryKey: ['upcoming-showings'] });
  }, [queryClient]);

  useDomainEvents({
    'case.message_received': invalidateDashboard,
    'lead.decided': invalidateDashboard,
    'appointment_proposal.created': invalidateDashboard,
    'appointment_proposal.confirmed': invalidateDashboard,
    'appointment_proposal.rejected': invalidateDashboard,
    'appointment_proposal.cancelled': invalidateDashboard,
    'appointment_proposal.rescheduled': invalidateDashboard,
    'showing.scheduled': invalidateDashboard,
    'applicant.submitted': invalidateDashboard,
  });

  const { data: inboxData, isLoading: inboxLoading } = useQuery({
    queryKey: ['inbox-unresponded'],
    queryFn: () => fetchInboxUnresponded(),
    staleTime: 60_000,
  });

  const { data: activityData, isLoading: activityLoading, isError: activityError, refetch: activityRefetch } = useQuery({
    queryKey: ['dashboard-activity', timeWindow],
    queryFn: () => fetchDashboardActivity(computeSince(timeWindow)),
    staleTime: 60_000,
  });

  const { data: showingsData, isLoading: showingsLoading, isError: showingsError, refetch: showingsRefetch } = useQuery({
    queryKey: ['upcoming-showings', showingsWindow],
    queryFn: () => fetchUpcomingShowings(computeWindowParam(showingsWindow)),
    staleTime: 60_000,
  });

  const inboxItems = (inboxData?.items ?? []).slice(0, 5);
  const activityItems = activityData?.items ?? [];
  const showingsItems = showingsData?.items ?? [];

  return (
    <div
      data-testid="leasing-dashboard-page"
      className="max-w-3xl mx-auto pb-6 px-[var(--page-padding-x)] pt-6"
    >
      <DashboardSection
        title="Unread Messages"
        viewAllTo="/leasing/inbox"
        viewAllLabel="View all"
        isLoading={inboxLoading}
        isEmpty={inboxItems.length === 0}
        emptyMessage="All caught up — no unread messages."
      >
        {inboxItems.map((item) => (
          <InboxPreviewRow key={item.leadId} item={item} />
        ))}
      </DashboardSection>

      <UpcomingShowingsSection
        timeWindow={showingsWindow}
        setTimeWindow={setShowingsWindow}
        isLoading={showingsLoading}
        isError={showingsError}
        refetch={showingsRefetch}
        items={showingsItems}
      />

      <RecentActivitySection
        timeWindow={timeWindow}
        setTimeWindow={setTimeWindow}
        isLoading={activityLoading}
        isError={activityError}
        refetch={activityRefetch}
        items={activityItems}
      />
    </div>
  );
}
